// src/components/Exercise/StepByStep/mathExerciseTemplates.ts

export interface StepByStepTemplate {
  type: string;
  name: string;
  description: string;
  generator: (difficulty: number) => any;
}

// Linear equation solver: ax + b = c
export const linearEquationTemplate = {
  type: 'linear_equation',
  name: 'Lineaarinen yhtälö',
  description: 'Ratkaise lineaarinen yhtälö vaiheittain',
  generator: (difficulty: number = 1) => {
    const a = Math.floor(Math.random() * (difficulty * 5)) + 1;
    const b = Math.floor(Math.random() * (difficulty * 10)) + 1;
    const c = Math.floor(Math.random() * (difficulty * 20)) + 5;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const x = Math.floor((c - b) / a);
    
    return {
      exerciseType: 'step_by_step_math',
      problemStatement: `${a}x + ${b} = ${c}`,
      problemLatex: `${a}x + ${b} = ${c}`,
      difficulty,
      allowCalculator: difficulty > 3,
      timeLimit: difficulty * 10,
      hints: [
        'Siirrä vakiot toiselle puolelle',
        'Jaa molemmat puolet kerroimella',
        'Tarkista vastaus sijoittamalla takaisin'
      ],
      steps: [
        {
          id: 'step1',
          description: 'Vaihe 1: Siirrä vakiotermi oikealle puolelle',
          expression: `${a}x + ${b} - ${b} = ${c} - ${b}`,
          explanation: `Vähennä ${b} molemmilta puolilta`,
          userInput: '',
          isCorrect: null,
          hint: `Mitä jää jäljelle kun vähennetään ${b} molemmilta puolilta?`,
          allowedAnswers: [`${a}x = ${c - b}`, `${a}x=${c - b}`]
        },
        {
          id: 'step2', 
          description: 'Vaihe 2: Sievennä',
          expression: `${a}x = ${c - b}`,
          explanation: 'Laske oikea puoli',
          userInput: '',
          isCorrect: null,
          hint: `${c} - ${b} = ?`,
          allowedAnswers: [`${a}x = ${c - b}`, `${a}x=${c - b}`]
        },
        {
          id: 'step3',
          description: 'Vaihe 3: Ratkaise x',
          expression: `x = ${c - b}/${a}`,
          explanation: `Jaa molemmat puolet luvulla ${a}`,
          userInput: '',
          isCorrect: null,
          hint: `Jaa ${c - b} luvulla ${a}`,
          allowedAnswers: [`x = ${(c - b) / a}`, `x=${(c - b) / a}`, `${(c - b) / a}`]
        }
      ],
      finalAnswer: (c - b) / a
    };
  }
};

// Quadratic equation: ax² + bx + c = 0
export const quadraticEquationTemplate = {
  type: 'quadratic_equation',
  name: 'Toisen asteen yhtälö',
  description: 'Ratkaise toisen asteen yhtälö kaavalla',
  generator: (difficulty: number = 2) => {
    const a = 1;
    const b = Math.floor(Math.random() * 10) - 5;
    const c = Math.floor(Math.random() * 10) - 5;
    const discriminant = b * b - 4 * a * c;
    
    return {
      exerciseType: 'step_by_step_math',
      problemStatement: `${a}x² ${b >= 0 ? '+' : ''}${b}x ${c >= 0 ? '+' : ''}${c} = 0`,
      difficulty: difficulty + 1, // Quadratic is harder
      allowCalculator: true,
      timeLimit: 25,
      hints: [
        'Käytä toisen asteen yhtälön ratkaisukaavaa',
        'Diskriminantti D = b² - 4ac',
        'x = (-b ± √D) / (2a)'
      ],
      steps: [
        {
          id: 'step1',
          description: 'Vaihe 1: Tunnista kertoimet',
          expression: `a = ${a}, b = ${b}, c = ${c}`,
          explanation: 'Vertaa yhtälöä muotoon ax² + bx + c = 0',
          userInput: '',
          isCorrect: null,
          hint: 'x²:n kerroin on a, x:n kerroin on b, vakiotermi on c',
          allowedAnswers: [`a=${a}, b=${b}, c=${c}`, `a=${a},b=${b},c=${c}`]
        },
        {
          id: 'step2',
          description: 'Vaihe 2: Laske diskriminantti',
          expression: `D = b² - 4ac = ${b}² - 4·${a}·${c}`,
          explanation: 'Diskriminantti D = b² - 4ac',
          userInput: '',
          isCorrect: null,
          hint: `${b}² = ${b * b}, 4·${a}·${c} = ${4 * a * c}`,
          allowedAnswers: [`D = ${discriminant}`, `D=${discriminant}`, `${discriminant}`]
        },
        {
          id: 'step3',
          description: 'Vaihe 3: Sovella ratkaisukaava',
          expression: `x = (-${b} ± √${discriminant}) / (2·${a})`,
          explanation: 'Käytä kaavaa x = (-b ± √D) / (2a)',
          userInput: '',
          isCorrect: null,
          hint: 'Korvaa arvot ratkaisukaavaan',
          allowedAnswers: [`x = (${-b} ± √${discriminant}) / ${2 * a}`, `x=(${-b}±√${discriminant})/${2 * a}`]
        }
      ],
      finalAnswer: discriminant >= 0 ? 
        [(-b + Math.sqrt(discriminant)) / (2 * a), (-b - Math.sqrt(discriminant)) / (2 * a)] :
        'Ei reaaliratkaisu'
    };
  }
};

// Fraction arithmetic
export const fractionArithmeticTemplate = {
  type: 'fraction_arithmetic',
  name: 'Murtolukujen laskutoimitus',
  description: 'Laske murtolukuja vaiheittain',
  generator: (difficulty: number = 1) => {
    const num1 = Math.floor(Math.random() * 9) + 1;
    const den1 = Math.floor(Math.random() * 9) + 2;
    const num2 = Math.floor(Math.random() * 9) + 1;
    const den2 = Math.floor(Math.random() * 9) + 2;
    
    const operation = difficulty > 2 ? 
      ['+', '-', '*', '/'][Math.floor(Math.random() * 4)] : 
      ['+', '-'][Math.floor(Math.random() * 2)];
    
    const lcm = (a: number, b: number): number => {
      return Math.abs(a * b) / gcd(a, b);
    };
    
    const gcd = (a: number, b: number): number => {
      return b === 0 ? a : gcd(b, a % b);
    };
    
    let steps: any[] = [];
    let finalNum: number = 0;
    let finalDen: number = 1;
    
    if (operation === '+' || operation === '-') {
      const commonDen = lcm(den1, den2);
      const newNum1 = num1 * (commonDen / den1);
      const newNum2 = num2 * (commonDen / den2);
      
      finalNum = operation === '+' ? newNum1 + newNum2 : newNum1 - newNum2;
      finalDen = commonDen;
      
      steps = [
        {
          id: 'step1',
          description: 'Vaihe 1: Etsi yhteinen nimittäjä',
          expression: `PYN(${den1}, ${den2}) = ${commonDen}`,
          explanation: `Etsi pienin yhteinen nimittäjä nimittäjille ${den1} ja ${den2}`,
          userInput: '', isCorrect: null,
          hint: `Pienin yhteinen nimittäjä on ${commonDen}`,
          allowedAnswers: [`${commonDen}`, `PYN = ${commonDen}`]
        },
        {
          id: 'step2',
          description: 'Vaihe 2: Laajenna murtoluvut',
          expression: `${num1}/${den1} = ${newNum1}/${commonDen}, ${num2}/${den2} = ${newNum2}/${commonDen}`,
          explanation: 'Laajenna molemmat murtoluvut yhteiseen nimittäjään',
          userInput: '', isCorrect: null,
          hint: `${num1}×${commonDen / den1} = ${newNum1}, ${num2}×${commonDen / den2} = ${newNum2}`,
          allowedAnswers: [`${newNum1}/${commonDen}, ${newNum2}/${commonDen}`, `${newNum1}/${commonDen} ja ${newNum2}/${commonDen}`]
        },
        {
          id: 'step3',
          description: 'Vaihe 3: Laske osoittajat',
          expression: `${newNum1}/${commonDen} ${operation} ${newNum2}/${commonDen} = ${finalNum}/${finalDen}`,
          explanation: `${operation === '+' ? 'Laske osoittajat yhteen' : 'Vähennä osoittajat'}`,
          userInput: '', isCorrect: null,
          hint: `${newNum1} ${operation} ${newNum2} = ${finalNum}`,
          allowedAnswers: [`${finalNum}/${finalDen}`, `${finalNum}/${commonDen}`]
        }
      ];
    }
    
    return {
      exerciseType: 'step_by_step_math',
      problemStatement: `${num1}/${den1} ${operation} ${num2}/${den2}`,
      difficulty,
      allowCalculator: difficulty > 2,
      timeLimit: difficulty * 8,
      hints: [
        'Muista etsiä yhteinen nimittäjä',
        'Laajenna murtoluvut ennen laskemista',
        'Sievennä lopputulos aina'
      ],
      steps,
      finalAnswer: `${finalNum}/${finalDen}`
    };
  }
};

// System of equations (2x2)
export const systemOfEquationsTemplate = {
  type: 'system_2x2',
  name: 'Yhtälöpari',
  description: 'Ratkaise kahden yhtälön yhtälöpari',
  generator: (difficulty: number = 3): any => {
    // Simple system: ax + by = c, dx + ey = f
    const a = Math.floor(Math.random() * 5) + 1;
    const b = Math.floor(Math.random() * 5) + 1;
    const d = Math.floor(Math.random() * 5) + 1;
    const e = Math.floor(Math.random() * 5) + 1;
    
    // Ensure unique solution
    if (a * e === b * d) {
      return systemOfEquationsTemplate.generator(difficulty);
    }
    
    const x = Math.floor(Math.random() * 10) + 1;
    const y = Math.floor(Math.random() * 10) + 1;
    const c = a * x + b * y;
    const f = d * x + e * y;
    
    return {
      exerciseType: 'step_by_step_math',
      problemStatement: `${a}x + ${b}y = ${c}\n${d}x + ${e}y = ${f}`,
      difficulty: difficulty + 1,
      allowCalculator: true,
      timeLimit: 30,
      hints: [
        'Käytä eliminointimenetelmää',
        'Kerro yhtälöitä sopivilla luvuilla',
        'Eliminoi toinen muuttuja'
      ],
      steps: [
        {
          id: 'step1',
          description: 'Vaihe 1: Eliminoi y-termi',
          expression: `Kerro 1. yhtälö luvulla ${e}, 2. yhtälö luvulla -${b}`,
          explanation: 'Valitse kertoimet niin että y-termit kumoavat toisensa',
          userInput: '', isCorrect: null,
          hint: `${e} × (${a}x + ${b}y = ${c}) ja -${b} × (${d}x + ${e}y = ${f})`,
          allowedAnswers: [`${e * a}x + ${e * b}y = ${e * c}, ${-b * d}x + ${-b * e}y = ${-b * f}`]
        },
        {
          id: 'step2',
          description: 'Vaihe 2: Laske yhtälöt yhteen',
          expression: `${e * a + (-b * d)}x = ${e * c + (-b * f)}`,
          explanation: 'Laske muunnetut yhtälöt yhteen',
          userInput: '', isCorrect: null,
          hint: `Y-termit kumoavat toisensa: ${e * b} + (${-b * e}) = 0`,
          allowedAnswers: [`${e * a - b * d}x = ${e * c - b * f}`]
        },
        {
          id: 'step3',
          description: 'Vaihe 3: Ratkaise x',
          expression: `x = ${e * c - b * f}/${e * a - b * d}`,
          explanation: 'Jaa molemmat puolet x:n kertoimella',
          userInput: '', isCorrect: null,
          hint: `x = ${x}`,
          allowedAnswers: [`x = ${x}`, `${x}`]
        },
        {
          id: 'step4',
          description: 'Vaihe 4: Ratkaise y',
          expression: `${a} × ${x} + ${b}y = ${c}`,
          explanation: 'Sijoita x:n arvo ensimmäiseen yhtälöön',
          userInput: '', isCorrect: null,
          hint: `${a * x} + ${b}y = ${c}, joten ${b}y = ${c - a * x}`,
          allowedAnswers: [`y = ${y}`, `${y}`]
        }
      ],
      finalAnswer: `x = ${x}, y = ${y}`
    };
  }
};

export const mathTemplates = {
  linearEquationTemplate,
  quadraticEquationTemplate,
  fractionArithmeticTemplate,
  systemOfEquationsTemplate
};

export default mathTemplates;